import EditIcon from '@mui/icons-material/Edit';
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { postGLDeptForm } from '../../../../api/invoicing';
import { getGLs, getVendors } from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import FormTextWithValue from '../../../../reusable-components/form/FormTextWithValue';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { ScopeGuard } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';

NewGLDeptModal.propTypes = {
  row: PropTypes.object,
  edit: PropTypes.bool,
  setOpen: PropTypes.func,
  open: PropTypes.bool,
};

export default function NewGLDeptModal({ row = null, edit = false, open = false, setOpen }) {
  const dispatch = useDispatch();
  const [edited, setEdited] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [checked, setChecked] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { data: { invoicingGLs, invoicingGLCategories, editableGLFacilities } } = useSelector((state) => state.invoicing);

  const facOptions = useMemo(() => editableGLFacilities, [editableGLFacilities]);

  const existingGLs = useMemo(() =>
    invoicingGLCategories?.map((v) => ({ facilityId: v.facilityId, category: v.category?.toLowerCase()?.trim() }))
    , [invoicingGLs]);

  const [value, setValue] = useState({
    id: '',
    categoryId: '',
    category: '',
    categoryDesc: '',
    facility: '',
  });

  const handleSave = async () => {
    const response = await postGLDeptForm(value);
    if (response.data.error) {
      enqueueSnackbar(`Error: ${response.data.error}`, {
        variant: 'error',
      });
    } else if (response.status === 200) {
      enqueueSnackbar(`Successfully added ${response.data.code}`, {
        variant: 'success',
      });
      dispatch(getVendors());
      dispatch(getGLs());
      setOpen(false);
    } else
      enqueueSnackbar(`Error: Could not add vendor`, {
        variant: 'error',
      });
  };

  const handleClickOpen = () => {
    setValue({
      id: edit ? row.categoryId : '',
      categoryId: edit ? row.categoryId : '',
      categoryDesc: edit ? row.categoryDesc : '',
      category: edit ? row.category : '',
      facility: edit ? facOptions?.find(f => f.value === row.facilityId) ?? '' : '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setValue({
      id: '',
      categoryId: '',
      category: '',
      categoryDesc: '',
      facility: '',
    });
    setOpen(false);
  };

  const vendorError = value.category === ''
    || row?.category !== value.category
    && existingGLs?.find(vendName => vendName.category === value.category?.toLowerCase().trim()
      && (vendName.facilityId === facOptions?.find(o => o.value === value.facility?.value)?.value
        || vendName.facilityId === 0));

  const facilityError = value.facility === '' || value.facility === undefined || value.facility === null;

  function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  useEffect(() => {
    if (open) {
      const onEdit = !Object.keys(value).every((key) => {
        let value1 = value?.[key];
        let value2 = edit ? row[key] : '';
        if (isObject(value1)) value1 = value1.label;
        if (isObject(value2)) value2 = value2.label;

        if (!Number.isNaN(+value2)) return Number(value1) === Number(value2);
        return (value1 === null && value2 === '') || (value1 === '' && value2 === null) || value1 === value2;
      });
      setEdited(onEdit);
    }
  }, [value]);

  useEffect(() => {
    if (!edit) setChecked(!!value?.facility?.extraValue)
  }, [value.facility])

  useEffect(() => {
    if (edit) setChecked(!!value?.categoryId)
  }, [open])

  useEffect(() => {
    if (checked === false) setValue((v) => ({ ...v, category: '', categoryDesc: '', categoryId: 0, deleteCats: [] }))
  }, [checked])

  return (
    <ScopeGuard scopes={['Edit-Invoice-GLs']} allowAdmin>
      {edit && !row.glCode ? (
        <Tooltip title="edit department">
          <IconButton variant="outlined" sx={{ mr: 1 }} size="small" color="secondary" onClick={handleClickOpen}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <></>
      )}

      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }} maxWidth="xl">
        <DialogTitle sx={{ textAlign: 'center' }}>
          {edit ? 'Edit Department' : 'New Department'}
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Grid container direction="column" spacing={2} sx={{ px: 2 }}>
              <Grid item>
                {edited && (vendorError || facilityError) && (
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {value.category === '' ? 'Missing GL' : vendorError && 'Department already exists'}
                    {vendorError && facilityError && ' & '}
                    {facilityError && 'Missing facility'}
                  </Alert>
                )}
              </Grid>
              <Grid item>
                <Box flex={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Facility *
                  </Typography>
                  <Autocomplete
                    size="small"
                    fullWidth
                    value={value.facility}
                    isOptionEqualToValue={(option, value) => option?.id === value?.facility?.id}
                    options={facOptions || []}
                    onChange={(_, newValue) => setValue({ ...value, facility: newValue })}
                    renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                    sx={{
                      width: '100%',
                      mt: 1,
                      '& legend': { display: 'none' },
                      '& fieldset': { top: 0 },
                      '& .MuiFormLabel-root ': { display: 'none' },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item>
                <Grid container spacing={2}>
                  <FormTextWithValue
                    value={value}
                    title="Department Code"
                    flex={6}
                    name="category"
                    setValue={setValue}
                  />
                  <FormTextWithValue
                    value={value}
                    title="Description"
                    flex={6}
                    name="categoryDesc"
                    setValue={setValue}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            disabled={vendorError || facilityError || (checked && categoryError)}
            sx={{ width: 150 }}
            onClick={handleSave}
          >
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </ScopeGuard>
  );
}
